import React from "react";
import { LoginlessHeader } from "app/components/Chat";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "./StatusView.scss";
import completedCheckInIcon from "resources/icons/completed-check-in.png";
import { getAuthenticatedAppUrl } from "./helpers";

interface StatusViewProps {
  // Content props for customization
  title?: string;
  message?: string;
  buttonText?: string;
  icon?: string;
  iconColor?: "red" | "yellow" | "green" | "default";
  iconAlt?: string;

  // Legacy props for backward compatibility
  nextCheckInAvailable?: string; // Simple date string in YYYY-MM-DD format

  // Deep linking
  threadId?: string; // Thread ID for deep linking to conversation in authenticated app
}

// Helper function to format YYYY-MM-DD date string to readable format
const formatDateOnly = (dateString?: string): string | null => {
  if (!dateString) return null;

  try {
    // Handle YYYY-MM-DD format by creating date object
    const date = new Date(dateString + "T00:00:00"); // Add time to avoid timezone issues
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return null; // Return null if parsing fails
  }
};

export default function StatusView({
  title,
  message,
  buttonText,
  icon,
  iconColor = "default",
  iconAlt,
  nextCheckInAvailable,
  threadId,
}: Readonly<StatusViewProps>) {
  // TODO (IMPORTANT): Need to update formattedNextDate, to be cadence agnostic
  // Create completion message with or without next check-in date (legacy behavior)
  const formattedNextDate = formatDateOnly(nextCheckInAvailable);
  const defaultMessage = formattedNextDate
    ? `Thank you for completing your Manager Check-in! Your next check-in will be available on ${formattedNextDate}. You'll receive an email once it's time to complete another check-in.`
    : "Thank you for completing your Manager Check-in! You'll receive an email once it's time to complete another check-in.";

  // Use provided props or fall back to defaults
  const displayTitle = title || "You've already completed your check-in.";
  const displayMessage = message || defaultMessage;
  const displayButtonText = buttonText || "Back to Develop by Criteria";
  const displayIcon = icon || completedCheckInIcon; // Default to original image for completed state

  return (
    <div className="status-view">
      {/* Deep link the header's "Go to Develop" button too, so it lands
          on the same conversation as the "Back to Develop" button below. */}
      <LoginlessHeader threadId={threadId} />

      {/* Main Content */}
      <div className="status-view__content">
        <div className="status-view__text">
          {displayIcon.startsWith("http") ||
          displayIcon.includes(".") ||
          displayIcon === completedCheckInIcon ? (
            <img
              alt="Status icon"
              className="status-view__icon"
              src={displayIcon}
            />
          ) : (
            <div
              className={`status-view__icon status-view__icon--fontawesome status-view__icon--${iconColor}`}
            >
              <FontAwesomeIcon
                icon={["far", displayIcon as any]}
                style={{ width: "60px", height: "60px" }}
              />
            </div>
          )}

          <p className="status-view__title">{displayTitle}</p>

          <p className="status-view__message">{displayMessage}</p>
        </div>

        <button
          className="status-view__button"
          onClick={() => {
            // Deep link to conversation in authenticated app if threadId is available
            const url = getAuthenticatedAppUrl(threadId);
            globalThis.location.href = url;
          }}
        >
          {displayButtonText}
        </button>
      </div>
    </div>
  );
}
